import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AlarmClockOff, CheckCheck, Clock, ExternalLink, Hash, Mail, MailOpen, MessageCircle, RotateCcw } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import type { Tables } from '@/lib/store';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import { markNotifications } from '@/lib/actions';
import { formatWhen, formatWhenLong } from '@/lib/format';
import { useOpenTask } from '@/lib/nav';
import { StatusIcon, statusProgress } from '@/components/fields/Status';
import { PriorityFlag } from '@/components/fields/Priority';
import { Avatar } from '@/components/ui/Avatar';
import { Tooltip } from '@/components/ui/Tooltip';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu, toast } from '@/components/ui/overlays';
import { channelTitle, type InboxGroup, type InboxTab } from './model';
import { describeNotification, kindIcon } from './describe';

export function TaskStatusIcon({ db, task, size = 16 }: { db: Tables; task: Task; size?: number }) {
  const status = statusOf(db, task);
  return <StatusIcon type={status.type} color={status.color} size={size} progress={statusProgress(effectiveStatuses(db, task.listId), status)} />;
}

export interface InboxActions {
  toggleRead: (g: InboxGroup) => void;
  later: (g: InboxGroup) => void;
  unLater: (g: InboxGroup) => void;
  clear: (g: InboxGroup) => void;
  restore: (g: InboxGroup) => void;
}

const ids = (g: InboxGroup) => g.items.map((n) => n.id);

export const inboxActions: InboxActions = {
  toggleRead(g) {
    if (g.unread) markNotifications(g.items.filter((n) => !n.readAt).map((n) => n.id), { readAt: Date.now() });
    else markNotifications([g.latest.id], { readAt: null });
  },
  later(g) {
    const list = ids(g);
    markNotifications(list, { laterAt: Date.now() });
    toast('Saved for later', { label: 'Undo', onClick: () => markNotifications(list, { laterAt: null }) });
  },
  unLater(g) {
    markNotifications(ids(g), { laterAt: null });
  },
  clear(g) {
    const list = ids(g);
    markNotifications(list, { clearedAt: Date.now() });
    toast('Notification cleared', { label: 'Undo', onClick: () => markNotifications(list, { clearedAt: null }) });
  },
  restore(g) {
    markNotifications(ids(g), { clearedAt: null, laterAt: null });
  },
};

function RowMenu({ group, tab, onOpen, close }: { group: InboxGroup; tab: InboxTab; onOpen: () => void; close: () => void }) {
  const a = inboxActions;
  return (
    <Menu style={{ width: 220 }}>
      <MenuItem icon={<ExternalLink size={15} />} label="Open" onClick={() => (close(), onOpen())} />
      <MenuSeparator />
      <MenuItem
        icon={group.unread ? <MailOpen size={15} /> : <Mail size={15} />}
        label={group.unread ? 'Mark as read' : 'Mark as unread'}
        onClick={() => (close(), a.toggleRead(group))}
      />
      {tab === 'later' ? (
        <MenuItem icon={<AlarmClockOff size={15} />} label="Move back to Inbox" onClick={() => (close(), a.unLater(group))} />
      ) : tab !== 'cleared' ? (
        <MenuItem icon={<Clock size={15} />} label="Later" onClick={() => (close(), a.later(group))} />
      ) : null}
      {tab === 'cleared' ? (
        <MenuItem icon={<RotateCcw size={15} />} label="Restore" onClick={() => (close(), a.restore(group))} />
      ) : (
        <MenuItem icon={<CheckCheck size={15} />} label="Clear" onClick={() => (close(), a.clear(group))} />
      )}
    </Menu>
  );
}

function ActionButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip label={label}>
      <button
        className="ib-action"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

export const InboxRow = memo(function InboxRow({ group, tab, db, me, ws }: { group: InboxGroup; tab: InboxTab; db: Tables; me: string; ws: string }) {
  const openTask = useOpenTask();
  const navigate = useNavigate();
  const n = group.latest;
  const task = group.taskId ? db.tasks[group.taskId] : undefined;
  const KindIcon = kindIcon(n);
  const channel = group.channelId ? db.channels[group.channelId] : undefined;
  const title = task ? task.name : group.channelId ? channelTitle(db, group.channelId, me) : 'Notification';
  const actor = db.users[n.actorId];
  const showFlag = !!task && (task.priority === 1 || task.priority === 2);

  const open = () => {
    const unread = group.items.filter((x) => !x.readAt).map((x) => x.id);
    if (unread.length) markNotifications(unread, { readAt: Date.now() });
    if (task) openTask(task.id);
    else if (group.channelId) {
      const messageId = typeof n.data?.messageId === 'string' ? n.data.messageId : null;
      navigate(`/${ws}/chat/${group.channelId}${messageId ? `?m=${messageId}` : ''}`);
    }
  };

  return (
    <div
      className={clsx('ib-row', { unread: group.unread })}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) open();
      }}
      onContextMenu={(e) => openContextMenu(e, (close) => <RowMenu group={group} tab={tab} onOpen={open} close={close} />)}
    >
      <span className="ib-row-icon">
        {task ? (
          <TaskStatusIcon db={db} task={task} />
        ) : channel?.kind === 'dm' ? (
          <MessageCircle size={15} className="faint" />
        ) : (
          <Hash size={15} className="faint" />
        )}
      </span>
      <span className="ib-row-title truncate">{title}</span>
      <span className="ib-row-actor">
        <Tooltip label={actor?.name ?? 'Someone'}>
          <span className="ib-actor">
            <Avatar userId={n.actorId} size={20} />
            <span className="ib-actor-kind">
              <KindIcon size={9} strokeWidth={2.5} />
            </span>
          </span>
        </Tooltip>
      </span>
      <span className="ib-row-desc truncate">
        {/* blocker notices read "Task is blocked by <task>"; the avatar still shows who linked it */}
        <span className="ib-actor-name">{n.kind === 'blocked' ? 'Task' : n.actorId === me ? 'You' : (actor?.name ?? 'Someone')}</span> {describeNotification(db, n, me)}
      </span>
      <span className="ib-row-meta">
        {showFlag && (
          <span className="ib-row-flag">
            <PriorityFlag priority={task!.priority} size={13} />
          </span>
        )}
        <Tooltip label={`${group.items.length} notification${group.items.length === 1 ? '' : 's'}`}>
          <span className="ib-count">{group.items.length > 99 ? '99+' : group.items.length}</span>
        </Tooltip>
        <Tooltip label={formatWhenLong(n.createdAt)}>
          <span className="ib-time">{formatWhen(n.createdAt)}</span>
        </Tooltip>
      </span>
      <span className="ib-row-actions" onClick={(e) => e.stopPropagation()}>
        <ActionButton label={group.unread ? 'Mark as read' : 'Mark as unread'} onClick={() => inboxActions.toggleRead(group)}>
          {group.unread ? <MailOpen size={15} /> : <Mail size={15} />}
        </ActionButton>
        {tab === 'later' ? (
          <ActionButton label="Move back to Inbox" onClick={() => inboxActions.unLater(group)}>
            <AlarmClockOff size={15} />
          </ActionButton>
        ) : tab !== 'cleared' ? (
          <ActionButton label="Later" onClick={() => inboxActions.later(group)}>
            <Clock size={15} />
          </ActionButton>
        ) : null}
        {tab === 'cleared' ? (
          <ActionButton label="Restore" onClick={() => inboxActions.restore(group)}>
            <RotateCcw size={15} />
          </ActionButton>
        ) : (
          <ActionButton label="Clear" onClick={() => inboxActions.clear(group)}>
            <CheckCheck size={15} />
          </ActionButton>
        )}
      </span>
    </div>
  );
});
